import React, { useState, useRef } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
} from '@mui/joy';
import {
  UploadSimple as Upload,
  FileText,
  CheckCircle,
  XCircle as AlertCircle,
  Ruler,
  House as Home,
} from '@phosphor-icons/react';
import {
  useUploadMeasurementPdf,
  type DetailedMeasurement,
} from '../../hooks/measurements/useDetailedMeasurements';

interface MeasurementPdfUploadModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId?: number;
  jobId?: number;
  measurementId?: number;
  replaceExisting?: boolean;
  existingMeasurementLabel?: string;
  onSuccess?: (data: DetailedMeasurement) => void;
}

export const MeasurementPdfUploadModal: React.FC<MeasurementPdfUploadModalProps> = ({
  open,
  onClose,
  opportunityId,
  jobId,
  measurementId,
  replaceExisting = false,
  existingMeasurementLabel,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadMeasurementPdf();

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF files are supported');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const result = await uploadMutation.mutateAsync({
        file: selectedFile,
        measurementId,
        opportunityId,
        jobId,
        replaceExisting,
      });

      if (result.success && onSuccess) {
        onSuccess(result.data);
      }

      // Close modal on success after a brief delay
      if (result.success) {
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    uploadMutation.reset();
    onClose();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const getSourceTypeColor = (type: string): 'primary' | 'success' | 'warning' | 'neutral' => {
    switch (type) {
      case 'eagleview':
        return 'primary';
      case 'gaf_quick_measure':
        return 'success';
      case 'roofr':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const uploadErrorDetail =
    (uploadMutation.error as { response?: { data?: { detail?: string; error?: string } } } | null)
      ?.response?.data?.detail ||
    (uploadMutation.error as { response?: { data?: { detail?: string; error?: string } } } | null)
      ?.response?.data?.error;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg" sx={{ maxWidth: { xs: 'calc(100vw - 16px)', sm: 650 }, width: { xs: 'auto', sm: '100%' }, maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Ruler size={24} />
            {replaceExisting ? 'Replace Measurement Report' : 'Upload Measurement Report'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            Upload a measurement PDF from EagleView, GAF Quick Measure, Roofr, or other providers.
            Our AI will automatically extract the measurement data.
          </Typography>

          {replaceExisting && !uploadMutation.isSuccess && (
            <Alert
              color="warning"
              startDecorator={<AlertCircle size={20} />}
              sx={{ mb: 2 }}
            >
              Uploading a new PDF report will permanently replace and delete the{' '}
              {existingMeasurementLabel || 'existing measurement'} on this opportunity.
            </Alert>
          )}

          {/* Success State */}
          {uploadMutation.isSuccess && uploadMutation.data?.success && (
            <Alert
              color="success"
              startDecorator={<CheckCircle size={20} />}
              sx={{ mb: 2 }}
            >
              Measurement parsed successfully!
            </Alert>
          )}

          {/* Error State */}
          {(uploadMutation.isError || (uploadMutation.data && !uploadMutation.data.success)) && (
            <Alert
              color="warning"
              startDecorator={<AlertCircle size={20} />}
              sx={{ mb: 2 }}
            >
              {uploadMutation.data?.message ||
                uploadErrorDetail ||
                'Failed to parse measurement. The PDF was saved but may need manual review.'}
            </Alert>
          )}

          {/* Parsed Results */}
          {uploadMutation.data?.data && uploadMutation.data.data.parsing_status === 'completed' && (
            <Card variant="soft" sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography level="title-md" startDecorator={<Home size={18} />}>
                    Parsed Results
                  </Typography>
                  <Chip
                    size="sm"
                    color={getSourceTypeColor(uploadMutation.data.data.source_type)}
                    variant="soft"
                  >
                    {uploadMutation.data.data.source_type_display}
                  </Chip>
                </Box>

                <Grid container spacing={2}>
                  <Grid xs={6} sm={4}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Total Area
                    </Typography>
                    <Typography level="body-md" fontWeight="lg">
                      {formatNumber(uploadMutation.data.data.total_area_sqft)} sq ft
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={4}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Squares
                    </Typography>
                    <Typography level="body-md" fontWeight="lg">
                      {formatNumber(uploadMutation.data.data.total_squares)}
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={4}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Facets
                    </Typography>
                    <Typography level="body-md" fontWeight="lg">
                      {uploadMutation.data.data.facet_count ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={4}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Pitch
                    </Typography>
                    <Typography level="body-md" fontWeight="lg">
                      {uploadMutation.data.data.predominant_pitch || '-'}
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={4}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Waste Factor
                    </Typography>
                    <Typography level="body-md" fontWeight="lg">
                      {uploadMutation.data.data.waste_factor
                        ? `${uploadMutation.data.data.waste_factor}%`
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={4}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Total Perimeter
                    </Typography>
                    <Typography level="body-md" fontWeight="lg">
                      {formatNumber(uploadMutation.data.data.total_perimeter_ft)} ft
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid xs={6} sm={3}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Ridges
                    </Typography>
                    <Typography level="body-sm">
                      {formatNumber(uploadMutation.data.data.total_ridges_ft)} ft
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Valleys
                    </Typography>
                    <Typography level="body-sm">
                      {formatNumber(uploadMutation.data.data.total_valleys_ft)} ft
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Eaves
                    </Typography>
                    <Typography level="body-sm">
                      {formatNumber(uploadMutation.data.data.total_eaves_ft)} ft
                    </Typography>
                  </Grid>
                  <Grid xs={6} sm={3}>
                    <Typography level="body-xs" textColor="text.tertiary">
                      Rakes
                    </Typography>
                    <Typography level="body-sm">
                      {formatNumber(uploadMutation.data.data.total_rakes_ft)} ft
                    </Typography>
                  </Grid>
                  {uploadMutation.data.data.total_hips_ft && (
                    <Grid xs={6} sm={3}>
                      <Typography level="body-xs" textColor="text.tertiary">
                        Hips
                      </Typography>
                      <Typography level="body-sm">
                        {formatNumber(uploadMutation.data.data.total_hips_ft)} ft
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Upload Area */}
          {!uploadMutation.isSuccess && (
            <Box
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.500' : 'neutral.300',
                borderRadius: 'md',
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: isDragOver ? 'primary.50' : 'transparent',
                '&:hover': {
                  borderColor: 'primary.300',
                  backgroundColor: 'background.level1',
                },
              }}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <>
                  <FileText size={48} style={{ marginBottom: 16, opacity: 0.7 }} />
                  <Typography level="body-lg" sx={{ mb: 1 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography level="body-sm" textColor="text.tertiary">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </>
              ) : (
                <>
                  <Upload size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                  <Typography level="body-lg" sx={{ mb: 1 }}>
                    Drop a measurement PDF here, or click to browse
                  </Typography>
                  <Typography level="body-sm" textColor="text.tertiary">
                    Supports: EagleView, GAF Quick Measure, Roofr, and more
                  </Typography>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
            </Box>
          )}

          {/* Progress */}
          {uploadMutation.isPending && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 1 }} />
              <Typography level="body-sm">Uploading and parsing PDF...</Typography>
              <Typography level="body-xs" textColor="text.tertiary">
                This may take a few seconds
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {uploadMutation.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!uploadMutation.isSuccess && (
            <Button
              variant="solid"
              color="primary"
              disabled={!selectedFile || uploadMutation.isPending}
              onClick={handleUpload}
              startDecorator={
                uploadMutation.isPending ? (
                  <CircularProgress size="sm" />
                ) : (
                  <Upload size={16} />
                )
              }
            >
              {uploadMutation.isPending ? 'Processing...' : 'Upload & Parse'}
            </Button>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default MeasurementPdfUploadModal;
